import { Alert, Button, Modal } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileCheck2, RefreshCw, Undo2 } from "lucide-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import { fetchItineraryCustomizationInfo } from "../../customization";
import { useItineraryLegacySyncWarning } from "../runtime/useItineraryLegacySyncWarning";
import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useUnsavedChangesGuard } from "../../../shared/navigation/useUnsavedChangesGuard";
import {
  ITINERARY_PERMISSIONS,
  type ItineraryListNavigationState,
  ItineraryCopyBar,
  ItineraryMoveBar,
  type ItineraryMoveResult,
  ItineraryDialogCloseButton,
  ItineraryEditorFeaturePage,
  ItineraryEditorLoading,
  ItineraryCustomizationInfoPage,
  buildItineraryListPreviewPath,
  buildItineraryEditPath,
  getItineraryPreviewPermissions,
  canReloadAfterItineraryStatusMutation,
  isItineraryStatusMutationOutcomeUnknown,
  itineraryPreviewRuntime,
  itinerarySaveErrorMessage,
  itineraryRequestFailureMessage,
  itinerarySaveErrorDiagnostic,
  itineraryStatusMutationErrorText,
  useItineraryCopyRuntime,
  useItineraryMoveRuntime,
  useItineraryEditorRuntime,
} from "..";

type StatusAction = "finalize" | "unfinalize" | "unreview";

const saveIncompleteMessage = "保存未完成：当前有尚未写入服务器的本地草稿，请先点击顶部保存，再执行状态操作。";

/** 原生行程编辑器路由页。 */
export function ItineraryEditorPage() {
  const { itineraryId = "" } = useParams();
  const location = useLocation();
  const activeRouteKeyRef = useRef<string | null>(location.key);
  useLayoutEffect(() => {
    activeRouteKeyRef.current = location.key;
    return () => { activeRouteKeyRef.current = null; };
  }, [location.key]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserQuery = useCurrentAuthUser();
  const hasUpdatePermission = canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.update);
  const draftDemandId = new URLSearchParams(location.search).get("draftDemandId") ?? undefined;
  const runtime = useItineraryEditorRuntime(itineraryId, { automaticTransportEnabled: hasUpdatePermission, draftDemandId });
  const persisted = runtime.document?.persisted !== false;
  const [localDraftDirty, setLocalDraftDirty] = useState(false);
  const [pendingStatusAction, setPendingStatusAction] = useState<StatusAction>();
  const [statusNotice, setStatusNotice] = useState<{
    type: "success" | "error";
    text: string;
    reloadLatest?: boolean;
    refreshMoved?: ItineraryMoveResult;
  }>();
  const [statusSyncRequiredForId, setStatusSyncRequiredForId] = useState<string>();
  const statusSyncRequired = statusSyncRequiredForId === itineraryId;
  const unsavedChangesGuard = useUnsavedChangesGuard(
    localDraftDirty || !["clean", "local_saved", "saved"].includes(runtime.saveStatus),
  );
  const { canPreview, canExport, canShare, canViewTable, canCopy, canMove, canFinalize }
    = getItineraryPreviewPermissions(currentUserQuery.data);
  const reloadStatusPage = async (targetId: string, successText: string) => {
    setStatusSyncRequiredForId(targetId);
    let synchronized: boolean;
    try {
      synchronized = await runtime.reload();
    } catch {
      synchronized = false;
    }
    if (!synchronized) {
      setStatusNotice({
        type: "error",
        text: "服务端状态已变更，但页面未能同步最新状态。请重新加载后再继续操作。",
        reloadLatest: true,
      });
      return false;
    }
    setStatusSyncRequiredForId(undefined);
    setStatusNotice({ type: "success", text: successText });
    return true;
  };
  const retryStatusPageReload = async () => {
    const targetId = statusSyncRequiredForId ?? itineraryId;
    await reloadStatusPage(targetId, "页面已重新加载并与服务端状态同步。");
  };
  const copyRuntime = useItineraryCopyRuntime({ sourceItineraryId: itineraryId, visible: canCopy });
  const handleMoved = async (moved: ItineraryMoveResult) => {
    try {
      await runtime.refreshMovedAssociation(moved);
      setStatusNotice({ type: "success", text: "行程及其核价、报价、确认件已移动，本地未保存内容已保留。" });
    } catch {
      setStatusNotice({ type: "error", text: "行程已移动，页面归属刷新失败；请重试刷新，本地草稿会保留。", refreshMoved: moved });
    }
  };
  const moveRuntime = useItineraryMoveRuntime({ itineraryId, onMoved: handleMoved });
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  const customizationProjectId = runtime.editor?.projectId?.trim() || "";
  const customizationDemandId = runtime.editor?.sourceDemandId?.trim() || "";
  const customizationQuery = useQuery({
    queryKey: ["itinerary", "customization-info", itineraryId, customizationProjectId, customizationDemandId],
    enabled: Boolean(itineraryId && runtime.editor),
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => draftDemandId ? runtime.loadDraftCustomizationInfo()
      : fetchItineraryCustomizationInfo(itineraryId),
  });
  const associationWarning = customizationQuery.isError
    ? "暂时查不到这条行程对应的定制需求和商机，请点“重新检查关联”再试一次。"
    : customizationQuery.data?.customizationLinked === false
      ? "这条行程还没有选好对应的定制需求和商机。请到定制管理补齐，否则旧系统可能无法更新这条行程。"
      : customizationQuery.data?.opportunityLinked === false
        ? "这条行程已经选好了定制需求，但这份需求还没有对应的商机。请到定制管理补齐后再保存，否则旧系统可能无法更新或打开这条行程。"
        : undefined;
  const missingAssociation = customizationQuery.data?.customizationLinked === false
    || customizationQuery.data?.opportunityLinked === false;
  const legacySync = useItineraryLegacySyncWarning(itineraryId, missingAssociation);
  const leaveDraftAddress = () => {
    if (!draftDemandId || activeRouteKeyRef.current !== location.key) return;
    const search = new URLSearchParams(location.search);
    search.delete("draftDemandId");
    unsavedChangesGuard.runWithNavigationBypass(() => navigate({
      pathname: location.pathname, search: search.size ? `?${search}` : "",
    }, { replace: true, state: location.state }));
  };
  const saveWithSyncCheck = async () => {
    const saved = await runtime.save();
    if (saved) {
      leaveDraftAddress();
      legacySync.checkAfterSave();
      setStatusNotice((notice) => notice?.type === "error" && notice.text === saveIncompleteMessage ? undefined : notice);
    }
    return saved;
  };
  const retrySaveWithSyncCheck = async () => {
    const saved = await runtime.retrySave();
    if (saved) {
      leaveDraftAddress();
      legacySync.checkAfterSave();
      setStatusNotice((notice) => notice?.type === "error" && notice.text === saveIncompleteMessage ? undefined : notice);
    }
  };
  const loadOutputEditor = useCallback(
    async () => (await itineraryPreviewRuntime.getEditor(itineraryId)).payload,
    [itineraryId, customizationDemandId],
  );
  const itineraryListState = (location.state as { itineraryListState?: ItineraryListNavigationState } | null)
    ?.itineraryListState;
  const exitToList = () => navigate("/product-lines/course-routes", {
    state: itineraryListState ? { itineraryListState } : undefined,
  });
  const isCurrentStatusPage = (routeKey: string) => activeRouteKeyRef.current === routeKey;
  const leaveRestrictedDraft = async (targetId: string, routeKey: string) => {
    // 旧操作仍清理对应行程缓存，但不干扰用户已经切换的页面。
    if (isCurrentStatusPage(routeKey)) exitToList();
    await queryClient.cancelQueries({ queryKey: ["itinerary", "editor", targetId] });
    await queryClient.cancelQueries({ queryKey: ["itinerary", "preview", targetId] });
    queryClient.removeQueries({ queryKey: ["itinerary", "editor", targetId] });
    queryClient.removeQueries({ queryKey: ["itinerary", "preview", targetId] });
    void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
  };
  const buildCopiedItineraryPath = (copiedItineraryId: string) => {
    const path = buildItineraryEditPath(copiedItineraryId);
    return embed ? `${path}?embed=1` : path;
  };
  const statusMutation = useMutation({
    mutationFn: async ({ action, targetId }: { action: StatusAction; targetId: string; routeKey: string }) => {
      if (action === "finalize"
        && (localDraftDirty || !await runtime.ensureSaved())) {
        throw new Error(saveIncompleteMessage);
      }
      if (action === "unreview") {
        if (!runtime.document) throw new Error(saveIncompleteMessage);
        return itineraryPreviewRuntime.setReview(targetId, runtime.document.revision, false);
      }
      const latest = await itineraryPreviewRuntime.getEditor(targetId);
      return action === "finalize"
        ? itineraryPreviewRuntime.finalize(targetId, latest.revision)
        : itineraryPreviewRuntime.unfinalize(targetId, latest.revision);
    },
    onSuccess: async (document, variables) => {
      if (variables.action === "unfinalize") {
        try {
          await itineraryPreviewRuntime.getEditor(variables.targetId);
        } catch (error) {
          if (isHttpClientError(error) && error.code === "ITINERARY_REVIEW_REQUIRED") {
            await leaveRestrictedDraft(variables.targetId, variables.routeKey);
            return;
          }
          // 其他读取失败仍交给原状态同步恢复流程处理。
        }
      }
      queryClient.setQueryData(["itinerary", "editor", variables.targetId], document);
      queryClient.setQueryData(["itinerary", "preview", variables.targetId], document);
      void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
      if (!isCurrentStatusPage(variables.routeKey)) return;
      if (variables.action === "unreview") {
        runtime.acceptReviewState(document);
        setStatusNotice({ type: "success", text: "已取消审稿，其他定稿人员暂不能预览该草稿。" });
        return;
      }
      await reloadStatusPage(
        variables.targetId,
        document.payload.status === "confirmed" ? "行程已定稿" : "已取消定稿，可继续编辑",
      );
    },
    onError: async (error, variables) => {
      if (error instanceof Error && error.message === saveIncompleteMessage) {
        if (isCurrentStatusPage(variables.routeKey)) {
          setStatusNotice({ type: "error", text: saveIncompleteMessage });
        }
        return;
      }
      if (isItineraryStatusMutationOutcomeUnknown(error)) {
        try {
          const latest = await itineraryPreviewRuntime.getEditor(variables.targetId);
          queryClient.setQueryData(["itinerary", "editor", variables.targetId], latest);
          queryClient.setQueryData(["itinerary", "preview", variables.targetId], latest);
          void queryClient.invalidateQueries({ queryKey: ["itinerary", "list"] });
          if (!isCurrentStatusPage(variables.routeKey)) return;
          const expectedStatus = variables.action === "finalize" ? "confirmed" : "draft";
          if (variables.action === "unreview") {
            if (latest.payload.status === "draft") {
              runtime.acceptReviewState(latest);
              setStatusNotice({ type: "success", text: "已核验服务器最新审稿状态，操作实际已完成。" });
              return;
            }
            throw error;
          }
          if (latest.payload.status === expectedStatus) {
            await reloadStatusPage(
              variables.targetId,
              "请求响应中断，已核验服务端最新状态：操作实际已完成。",
            );
            return;
          }
        } catch (readError) {
          if (isHttpClientError(readError) && readError.code === "ITINERARY_REVIEW_REQUIRED") {
            await leaveRestrictedDraft(variables.targetId, variables.routeKey);
            return;
          }
          // 回读失败时保留未知结果提示，由用户主动重新加载。
        }
        if (!isCurrentStatusPage(variables.routeKey)) return;
        setStatusSyncRequiredForId(variables.targetId);
        setStatusNotice({
          type: "error",
          text: "请求响应中断，仍无法确认状态操作结果，请重新加载最新版本。",
          reloadLatest: true,
        });
        return;
      }
      if (!isCurrentStatusPage(variables.routeKey)) return;
      const reloadLatest = canReloadAfterItineraryStatusMutation(error);
      if (reloadLatest) setStatusSyncRequiredForId(variables.targetId);
      setStatusNotice({
        type: "error",
        text: itineraryStatusMutationErrorText(error, variables.action),
        reloadLatest,
      });
    },
  });

  if (runtime.loading) return <ItineraryEditorLoading />;
  if (!runtime.editor) {
    const loadErrorView = itineraryLoadErrorView(runtime.loadError);
    return (
      <main className="itinerary-editor-empty">
        <div role="alert"><Alert type="error" content={loadErrorView.message} /></div>
        <div className="itinerary-editor-empty-actions">
          <Button icon={<ArrowLeft size={16} />} onClick={exitToList}>
            返回课程线路列表
          </Button>
          {loadErrorView.canRetry ? (
            <Button icon={<RefreshCw size={16} />}
              onClick={() => void (statusSyncRequired ? retryStatusPageReload() : runtime.reload())}>
              重新加载
            </Button>
          ) : null}
        </div>
      </main>
    );
  }
  if (runtime.editor.status === "archived") {
    return <Navigate replace to={buildItineraryListPreviewPath(runtime.editor.id)} state={location.state} />;
  }
  const editorDisabled = statusSyncRequired || statusMutation.isPending || runtime.reloading
    || moveRuntime.moving || Boolean(statusNotice?.refreshMoved)
    || ["confirmed", "archived"].includes(runtime.editor.status);
  const statusLifecycleAction = persisted && canFinalize && ["draft", "active", "reviewing", "confirmed"].includes(runtime.editor.status)
    ? runtime.editor.status === "confirmed" || runtime.editor.isConfirmed
      ? (
        <Button
          status="warning"
          icon={<Undo2 size={15} />}
          loading={statusMutation.isPending}
          disabled={statusSyncRequired || runtime.reloading}
          onClick={() => {
            setStatusNotice(undefined);
            setPendingStatusAction("unfinalize");
          }}
        >
          取消定稿
        </Button>
      )
      : (
        <Button
          className="itinerary-finalize-button"
          type="primary"
          icon={<FileCheck2 size={15} />}
          loading={statusMutation.isPending}
          disabled={statusSyncRequired || runtime.reloading}
          onClick={() => {
            setStatusNotice(undefined);
            setPendingStatusAction("finalize");
          }}
        >
          定稿
        </Button>
      )
    : null;
  const lifecycleActions = statusLifecycleAction;
  const reviewActions = hasUpdatePermission && runtime.editor.status === "reviewing"
    ? <Button
        status="warning"
        loading={statusMutation.isPending && statusMutation.variables?.action === "unreview"}
        disabled={editorDisabled || runtime.saveStatus === "saving" || runtime.contentSaveBlocked}
        onClick={() => {
          setStatusNotice(undefined);
          statusMutation.mutate({ action: "unreview", targetId: itineraryId, routeKey: location.key });
        }}
      >取消审稿</Button>
    : undefined;
  const copyActions = persisted && canCopy ? (
    <ItineraryCopyBar
      hasSearched={copyRuntime.hasSearched}
      items={copyRuntime.items}
      selectedDemandId={copyRuntime.selectedDemandId}
      loading={copyRuntime.loading}
      copying={copyRuntime.copying}
      errorMessage={copyRuntime.errorMessage}
      onSearch={copyRuntime.submitSearch}
      onSelect={copyRuntime.selectDemand}
      onConfirm={async (replaceExisting) => {
        const copied = await copyRuntime.confirm(replaceExisting);
        unsavedChangesGuard.runWithNavigationBypass(() => {
          navigate(canUsePermission(currentUserQuery.data, ITINERARY_PERMISSIONS.editorAccess)
            ? buildCopiedItineraryPath(copied.itineraryId)
            : buildItineraryListPreviewPath(copied.itineraryId), {
            state: itineraryListState ? { itineraryListState } : undefined,
          });
        });
      }}
      onRetry={copyRuntime.retry}
    />
  ) : undefined;

  return (
    <>
      {unsavedChangesGuard.blocker}
      <ItineraryEditorFeaturePage
        editor={runtime.editor}
        selectedDayId={runtime.selectedDayId}
        saveStatus={runtime.saveStatus}
        calculatingSegmentIds={runtime.calculatingSegmentIds}
        savedRevision={runtime.document?.revision}
        validationIssues={runtime.validationIssues}
        repairRequiredCount={runtime.repairRequiredCount}
        repairRemainingCount={runtime.repairRemainingCount}
        canUpdate={hasUpdatePermission}
        canCreatePoint={canUsePermission(currentUserQuery.data, "resource.point:manage")}
        canCreateStayArea={canUsePermission(currentUserQuery.data, "dictionary.stay_area:manage")}
        onCreateMainData={runtime.createMainData}
        editorDisabled={editorDisabled}
        canPreview={persisted && canPreview}
        canViewTable={canViewTable}
        canExport={persisted && canExport}
        canShare={persisted && canShare}
        enableInternalInteractions={persisted}
        embed={embed}
        errorMessage={itinerarySaveErrorMessage(runtime.saveError, runtime.editor)}
        errorDiagnostic={itinerarySaveErrorDiagnostic(runtime.saveError)}
        onChange={runtime.updateEditor}
        onSelectDay={runtime.selectDay}
        onSave={saveWithSyncCheck}
        onLoadOutputEditor={canPreview ? loadOutputEditor : undefined}
        onRetrySave={retrySaveWithSyncCheck}
        onReloadLatest={runtime.reloadLatest}
        onExit={exitToList}
        onEnrich={runtime.enrich}
        onRewritePendingSummaries={runtime.rewritePendingSummarySlots}
        summaryRewriting={runtime.summaryRewriting}
        contentSaveBlocked={runtime.contentSaveBlocked}
        summaryRewriteWarning={runtime.summaryRewriteWarning}
        onCompletePointDescriptions={runtime.completePointDescriptions}
        onListPointCandidates={runtime.listPointCandidates}
        onListMajorCandidates={runtime.listMajorCandidates}
        onGetRailwayOptions={runtime.getRailwayOptions}
        onGetFlightOptions={runtime.getFlightOptions}
        onGetDrivingRoute={runtime.getDrivingRoute}
        onGetWalkingRoute={runtime.getWalkingRoute}
        onLocalDraftChange={setLocalDraftDirty}
        copyActions={copyActions}
        onStartMove={persisted && canMove && runtime.editor.sourceDemandId && runtime.editor.status !== "archived"
          ? moveRuntime.open : undefined}
        onCancelMove={moveRuntime.cancel}
        movePending={moveRuntime.moving}
        moveActions={canMove && moveRuntime.active ? <ItineraryMoveBar items={moveRuntime.items}
          hasTargets={moveRuntime.hasTargets}
          selectedDemandId={moveRuntime.selectedDemandId} loading={moveRuntime.loading}
          moving={moveRuntime.moving} errorMessage={moveRuntime.errorMessage}
          onSearch={moveRuntime.search} onSelect={moveRuntime.selectDemand}
          onConfirm={moveRuntime.confirm} onCancel={moveRuntime.cancel} onRetry={moveRuntime.retry} /> : undefined}
        customizationPanel={!embed && customizationProjectId && customizationDemandId ? (
          <ItineraryCustomizationInfoPage info={customizationQuery.data}
            loading={customizationQuery.isFetching} error={customizationQuery.isError} />
        ) : undefined}
        associationNotice={!persisted || associationWarning ? <>
          {!persisted ? <Alert type="info" content="当前行程尚未保存到服务器。点击顶部保存后才会创建行程，关闭或仅查看不会影响智能体生成。" /> : null}
          {associationWarning ? <Alert type="warning" title="行程关联提醒"
          content={associationWarning}
          action={<Button size="small" loading={customizationQuery.isFetching}
            onClick={() => void customizationQuery.refetch()}>重新检查关联</Button>}
        /> : null}</> : undefined}
        legacySyncNotice={legacySync.warning ? <Alert type="warning" title="旧系统更新情况" style={{ flexShrink: 0 }}
          content={legacySync.warning}
          action={<Button size="small" loading={legacySync.checking}
            onClick={legacySync.checkAfterSave}>重新检查同步</Button>}
        /> : undefined}
        lifecycleActions={lifecycleActions}
        reviewActions={reviewActions}
        outputNotice={statusNotice ? {
          type: statusNotice.type,
          text: statusNotice.text,
          action: statusNotice.refreshMoved ? <Button size="small" onClick={() => void handleMoved(statusNotice.refreshMoved!)}>重试刷新归属</Button> : statusNotice.reloadLatest ? (
            <Button
              size="small"
              icon={<RefreshCw size={14} />}
              loading={statusMutation.isPending || runtime.reloading}
              onClick={() => void retryStatusPageReload()}
            >
              重新加载最新版本
            </Button>
          ) : undefined,
        } : undefined}
      />
      <Modal
        visible={Boolean(pendingStatusAction)}
        title={pendingStatusAction === "unfinalize" ? "确认取消定稿" : "确认定稿"}
        maskStyle={{ zIndex: 1299 }}
        closeIcon={(
          <ItineraryDialogCloseButton
            label={pendingStatusAction === "unfinalize" ? "关闭确认取消定稿" : "关闭确认定稿"}
          />
        )}
        okText={pendingStatusAction === "unfinalize" ? "确认取消定稿" : "确认定稿"}
        okButtonProps={pendingStatusAction === "unfinalize" ? { status: "warning" } : undefined}
        cancelText="取消"
        wrapStyle={{ zIndex: 1300 }}
        onOk={() => {
          const action = pendingStatusAction;
          setPendingStatusAction(undefined);
          if (action) statusMutation.mutate({ action, targetId: runtime.editor?.id ?? itineraryId, routeKey: location.key });
        }}
        onCancel={() => setPendingStatusAction(undefined)}
      >
        {pendingStatusAction === "unfinalize"
          ? "取消定稿后，行程将恢复为草稿并重新允许编辑。"
          : "系统会使用服务器上的最新版本定稿。本地草稿不会被隐式提交；如有修改，请先点击顶部保存。定稿后如需继续编辑，必须先取消定稿。"}
      </Modal>
    </>
  );
}

/** 将首次加载错误区分为不可恢复的深链接错误和可重试故障。 */
function itineraryLoadErrorView(error: unknown): { message: string; canRetry: boolean } {
  if (isHttpClientError(error)) {
    if (error.statusCode === 404 || error.code === "ITINERARY_NOT_FOUND") {
      return { message: "未找到该行程，可能已删除或链接无效。", canRetry: false };
    }
    if (error.statusCode === 403) {
      return { message: "当前账号无权查看该行程。", canRetry: false };
    }
    if (error.code === "ITINERARY_INVALID_GRAPH" || error.code === "ITINERARY_REFERENCE_INVALID") {
      return { message: itineraryRequestFailureMessage(error, "load"), canRetry: true };
    }
  }
  return { message: "行程暂时无法加载，请检查网络后重试。", canRetry: true };
}
